import './ReportFormFieldAttachment.scss';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { AttachmentIcon } from 'src/modules/common/icons/AttachmentIcon';
import { CrossIcon } from 'src/modules/common/icons/CrossIcon';
import { ErrorIcon } from 'src/modules/common/icons/ErrorIcon';
import { LoadingIcon } from 'src/modules/common/icons/LoadingIcon';
import { ReportAttachment } from 'src/modules/report/types/ReportAttachment';
import { ReportFileError } from 'src/modules/report/types/ReportFileError';

type Props = {
  readonly attachment: ReportAttachment;
  readonly onDelete: (attachment: ReportAttachment) => void;
};

export const ReportFormFieldAttachment = ({ attachment, onDelete }: Props): React.ReactElement => {
  const intl = useIntl();

  const handleDelete = useCallback(() => {
    onDelete(attachment);
  }, [attachment, onDelete]);

  return (
    <div className="bp-report-form-field-attachment">
      <div className="bp-report-form-field-attachment__icon">
        {attachment.state === 'success'
          ? <AttachmentIcon mimeType={attachment.file.mimeType}/>
          : null}
      </div>
      <div className="bp-report-form-field-attachment__file">
        {attachment.state === 'success' && (
          <a
            className="bp-report-form-field-attachment__file-link"
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
          >
            {attachment.file.fileName}
          </a>
        )}
        {attachment.state !== 'success' && (
          <span className="bp-report-form-field-attachment__file-name">
            {attachment.file.name}
          </span>
        )}
        {attachment.state === 'failure' && (
          <div className="bp-report-form-field-attachment__file-error">
            <ErrorInfo error={attachment.error}/>
          </div>
        )}
      </div>

      <div className="bp-report-form-field-attachment__action">
        {attachment.state === 'failure' && (
          <div className="bp-report-form-field-attachment__error">
            <ErrorIcon/>
          </div>
        )}
        {attachment.state === 'loading' && (
          <div className="bp-report-form-field-attachment__loader">
            <LoadingIcon/>
          </div>
        )}
        {attachment.state !== 'loading' && (
          <Tooltip
            id="report/form/appendix/files/delete"
            value={intl.formatMessage({ id: 'report/form/appendix/files/delete' })}
            place="left"
          >
            <Button
              iconOnly={true}
              size="sm"
              kind="ghost"
              className="bp-report-form-field-attachment__delete"
              onClick={handleDelete}
              disabled={false}
            >
              <CrossIcon/>
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const ErrorInfo: React.FC<{ error: ReportFileError }> = ({ error }) => {
  if (error.code === 'too-large') {
    return (
      <FormattedMessage
        id="report/form/appendix/upload/error/fileTooLarge"
        values={{ maxSize: error.maxSize }}
      />
    );
  }
  if (error.code === 'invalid') {
    return (
      <FormattedMessage
        id="report/form/appendix/upload/error/invalidFormat"
      />
    );
  }

  return (
    <FormattedMessage
      id="report/form/appendix/upload/error/notProcessed"
    />
  );
};
